import api from "../../utils/api";
import { getCaseStatusLabels } from "../../i18n/domain/cases";
import { accountStatusLabel } from "../../lib/accountStatus";
import { normalizeDeletionCaseReviewItem } from "../../shared/utils/deletionCaseReviews";
import {
  mergeReviewItemsWithPricing,
  normalizeCasePricing,
} from "../../shared/utils/deletionCasePricing";
import { normalizeOutcomeSummary } from "../../shared/utils/deletionCaseOutcomes";

export function getAdminStatusOptions(t) {
  const labels = getCaseStatusLabels(t);
  return Object.entries(labels).map(([value, label]) => ({ value, label }));
}

export function getRoleOptions(t) {
  return [
    { value: "user", label: t("admin.roleUser") },
    { value: "admin", label: t("admin.roleAdmin") },
  ];
}

export function getAccountStatusOptions(t) {
  return [
    { value: "trial", label: t("account.trial") },
    { value: "basic", label: t("account.basic") },
    { value: "premium", label: t("account.premium") },
    { value: "enterprise", label: t("account.enterprise") },
    { value: "expired", label: t("account.expired") },
  ];
}

/** @deprecated Use getAdminStatusOptions(t) */
export const ADMIN_STATUS_OPTIONS = [];

const asArray = (value) => (Array.isArray(value) ? value : []);

/** Billing package label for deletion-case table (from user account_status). */
export function packageLabelForAccountStatus(accountStatus, t) {
  return accountStatusLabel(accountStatus, t);
}

function normalizeAdminCase(raw) {
  const userDetail = raw.user_detail || {};
  const pricing = normalizeCasePricing(raw.pricing);
  const items = mergeReviewItemsWithPricing(
    asArray(raw.items).map((item) => normalizeDeletionCaseReviewItem(item)),
    pricing
  );
  return {
    id: raw.id,
    caseReference: raw.case_reference || "",
    userId: raw.user,
    violationType: raw.violation_type || "",
    justification: raw.justification || "",
    status: raw.status || "initiated",
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    reviewCount: raw.review_count ?? items.length,
    pricing,
    outcomeSummary: normalizeOutcomeSummary(raw.outcome_summary),
    submitter: {
      id: userDetail.id,
      fullName: userDetail.full_name || "",
      email: userDetail.email || "",
      companyName: userDetail.company_name || "",
      accountStatus: userDetail.account_status || "unpaid",
    },
    items,
  };
}

function normalizeAdminUser(raw) {
  return {
    id: raw.id,
    fullName: raw.full_name || "",
    email: raw.email || "",
    role: raw.role || "user",
    companyId: raw.company_id,
    companyName: raw.company_name || "",
    isVerified: raw.is_verified,
    isSuperuser: Boolean(raw.is_superuser),
    accountStatus: raw.account_status || "unpaid",
    trialEndsAt: raw.trial_ends_at || null,
    dateJoined: raw.date_joined,
    isAccountOwner: Boolean(raw.is_account_owner),
    isSubUser: Boolean(raw.is_sub_user),
    teamRole: raw.team_role || null,
    memberCount: raw.member_count ?? 0,
    ownerId: raw.owner_id ?? null,
    ownerEmail: raw.owner_email || "",
    ownerName: raw.owner_name || "",
  };
}

function normalizeDeletionCaseStats(raw) {
  const byStatus = raw?.by_status || {};
  return {
    total: raw?.total ?? 0,
    new: raw?.new ?? 0,
    pending: raw?.pending ?? 0,
    inProgress: raw?.in_progress ?? 0,
    completed: raw?.completed ?? 0,
    rejected: raw?.rejected ?? 0,
    newThisWeek: raw?.new_this_week ?? 0,
    byStatus: {
      initiated: byStatus.initiated ?? 0,
      started: byStatus.started ?? 0,
      legal_action: byStatus.legal_action ?? 0,
      completed: byStatus.completed ?? 0,
      rejected: byStatus.rejected ?? 0,
    },
  };
}

export async function fetchAdminDeletionCaseStats() {
  const data = await api.get("/api/admin/cases/stats/", { auth: true });
  return normalizeDeletionCaseStats(data);
}

export async function fetchAdminDeletionCases(params = {}) {
  const search = new URLSearchParams();
  if (params.page) search.set("page", String(params.page));
  if (params.pageSize) search.set("page_size", String(params.pageSize));
  if (params.status) search.set("status", params.status);
  if (params.statusGroup) search.set("status_group", params.statusGroup);
  if (params.createdWithinDays) {
    search.set("created_within_days", String(params.createdWithinDays));
  }
  if (params.violationType) search.set("violation_type", params.violationType);
  if (params.search) search.set("search", params.search);
  if (params.ordering) search.set("ordering", params.ordering);
  const qs = search.toString();
  const path = qs ? `/api/admin/cases/?${qs}` : "/api/admin/cases/";
  const data = await api.get(path, { auth: true });
  const list = asArray(data?.results ?? data);
  return {
    results: list.map(normalizeAdminCase),
    count: data?.count ?? list.length,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
  };
}

export async function updateAdminDeletionCase(caseId, payload = {}) {
  const body = {};
  if (payload.status != null) body.status = payload.status;
  if (payload.itemOutcomes?.length) {
    body.item_outcomes = payload.itemOutcomes.map((item) => ({
      id: item.id,
      deletion_outcome: item.deletionOutcome ?? item.deletion_outcome,
    }));
  }
  const data = await api.patch(`/api/admin/cases/${caseId}/`, body, {
    auth: true,
  });
  return normalizeAdminCase(data);
}

/** @deprecated Use updateAdminDeletionCase */
export async function updateAdminDeletionCaseStatus(caseId, status) {
  return updateAdminDeletionCase(caseId, { status });
}

export async function fetchAdminUsers(params = {}) {
  const search = new URLSearchParams();
  if (params.page) search.set("page", String(params.page));
  if (params.pageSize) search.set("page_size", String(params.pageSize));
  if (params.search) search.set("search", params.search);
  if (params.role) search.set("role", params.role);
  if (params.accountStatus) search.set("account_status", params.accountStatus);
  if (params.ordering) search.set("ordering", params.ordering);

  const qs = search.toString();
  const path = qs ? `/api/admin/users/?${qs}` : "/api/admin/users/";
  const data = await api.get(path, { auth: true });

  const list = asArray(data?.results ?? data);
  return {
    results: list.map(normalizeAdminUser),
    count: data?.count ?? list.length,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
  };
}

export async function impersonateAdminUser(userId) {
  const data = await api.post(
    `/api/admin/users/${userId}/impersonate/`,
    {},
    { auth: true }
  );
  return data;
}

export async function stopAdminImpersonation() {
  const data = await api.post("/api/admin/impersonate/stop/", {}, { auth: true });
  return data;
}

export async function updateAdminUserRole(userId, role) {
  const data = await api.patch(
    `/api/admin/users/${userId}/role/`,
    { role },
    { auth: true }
  );
  return normalizeAdminUser(data);
}

export async function updateAdminUserAccountStatus(userId, accountStatus) {
  const data = await api.patch(
    `/api/admin/users/${userId}/account-status/`,
    { account_status: accountStatus },
    { auth: true }
  );
  return normalizeAdminUser(data);
}

/** Same payload/validation as POST /api/users/register/ (superuser only). */
export async function registerAdminUser(payload) {
  const data = await api.post("/api/admin/users/register/", payload, {
    auth: true,
  });
  return normalizeAdminUser(data);
}

function normalizeAdminUserStats(raw) {
  return {
    total: raw?.total ?? 0,
    newThisWeek: raw?.new_this_week ?? 0,
    clients: raw?.clients ?? 0,
    admins: raw?.admins ?? 0,
    paid: raw?.paid ?? 0,
    unpaid: raw?.unpaid ?? 0,
    superusers: raw?.superusers ?? 0,
  };
}

function normalizeAdminReviewStats(raw) {
  const sentiment = raw?.sentiment;
  return {
    totalReviews: raw?.total_reviews ?? 0,
    companiesTracked: raw?.companies_tracked ?? 0,
    pendingModeration: raw?.pending_moderation ?? 0,
    flaggedThisWeek: raw?.flagged_this_week ?? 0,
    needsReply: raw?.needs_reply ?? 0,
    critical: raw?.critical ?? 0,
    newThisWeek: raw?.new_this_week ?? 0,
    sentiment: sentiment
      ? {
          positive: sentiment.positive ?? 0,
          neutral: sentiment.neutral ?? 0,
          negative: sentiment.negative ?? 0,
          positivePercent: sentiment.positive_percent ?? 0,
          neutralPercent: sentiment.neutral_percent ?? 0,
          negativePercent: sentiment.negative_percent ?? 0,
        }
      : null,
  };
}

function normalizeAdminUserDetail(raw) {
  const members = Array.isArray(raw.team_members) ? raw.team_members : [];
  return {
    ...normalizeAdminUser(raw),
    caseCount: raw.case_count ?? 0,
    reviewCount: raw.review_count ?? 0,
    teamMembers: members.map((m) => ({
      id: m.id,
      fullName: m.full_name || "",
      email: m.email || "",
      role: m.role || "member",
      isVerified: Boolean(m.is_verified),
      dateJoined: m.date_joined || null,
      joinedAt: m.joined_at || null,
    })),
  };
}

export async function fetchAdminUserStats() {
  const data = await api.get("/api/admin/users/stats/", { auth: true });
  return normalizeAdminUserStats(data);
}

export async function fetchAdminReviewStats() {
  const data = await api.get("/api/admin/reviews/stats/", { auth: true });
  return normalizeAdminReviewStats(data);
}

export async function fetchAdminUserDetail(userId) {
  const data = await api.get(`/api/admin/users/${userId}/`, { auth: true });
  return normalizeAdminUserDetail(data);
}

/** Extend (or reopen) a client's free trial by N days. */
export async function extendAdminUserTrial(userId, days) {
  const data = await api.post(
    `/api/admin/users/${userId}/extend-trial/`,
    { days: Number(days) },
    { auth: true }
  );
  return normalizeAdminUserDetail(data);
}

function normalizeActivityEvent(raw) {
  if (!raw) return null;
  const actor = raw.actor || null;
  return {
    id: raw.id,
    category: raw.category || "product",
    eventType: raw.event_type || "",
    success: raw.success !== false,
    summary: raw.summary || "",
    metadata: raw.metadata || {},
    ipAddress: raw.ip_address || null,
    userAgent: raw.user_agent || "",
    createdAt: raw.created_at || null,
    actor: actor
      ? {
          id: actor.id,
          fullName: actor.full_name || "",
          email: actor.email || "",
        }
      : null,
  };
}

/**
 * Per-user activity timeline for admin detail page.
 * @param {number|string} userId
 * @param {{ category?: 'auth'|'product'|'', limit?: number, offset?: number }} [params]
 */
export async function fetchAdminUserActivity(userId, params = {}) {
  const search = new URLSearchParams();
  if (params.category) search.set("category", params.category);
  if (params.limit != null) search.set("limit", String(params.limit));
  if (params.offset != null) search.set("offset", String(params.offset));
  const qs = search.toString();
  const path = qs
    ? `/api/admin/users/${userId}/activity/?${qs}`
    : `/api/admin/users/${userId}/activity/`;
  const data = await api.get(path, { auth: true });
  const results = asArray(data?.results).map(normalizeActivityEvent).filter(Boolean);
  return {
    count: data?.count ?? results.length,
    limit: data?.limit ?? params.limit ?? 50,
    offset: data?.offset ?? params.offset ?? 0,
    results,
  };
}

export function formatAdminError(err) {
  const data = err?.data;
  if (!data) return err?.message || "Anfrage fehlgeschlagen.";
  if (typeof data.detail === "string") return data.detail;
  if (data.role) {
    const msg = Array.isArray(data.role) ? data.role.join(" ") : String(data.role);
    return msg;
  }
  if (typeof data === "object") {
    const parts = Object.entries(data).map(([k, v]) => {
      const text = Array.isArray(v) ? v.join(" ") : String(v);
      return `${k}: ${text}`;
    });
    if (parts.length) return parts.join(" ");
  }
  return err?.message || "Anfrage fehlgeschlagen.";
}
